import { useCallback, useEffect, useState } from "react";
import { communityApi, type CommunityNote, type CommunityTopic } from "../../api/community";
import { libraryApi, type MaterialItem } from "../../api/library";
import { pickCoveredNotes, type MaterialTypeOption } from "./home-data";

export type HomeFeed = {
  featured: CommunityNote[];
  latest: CommunityNote[];
  /** 推荐与最新里所有带封面的作品（去重），供材质匹配真实应用场景。 */
  notePool: CommunityNote[];
  materials: MaterialItem[];
  materialTypes: MaterialTypeOption[];
  topics: CommunityTopic[];
  /** 材质列表已返回（首屏 3D 只依赖这一项，不必等待其余请求）。 */
  materialsReady: boolean;
  loading: boolean;
  error: string | null;
  reload: () => void;
};

function itemsOf<T>(result: PromiseSettledResult<{ items: T[] }>) {
  return result.status === "fulfilled" ? result.value.items : [];
}

function uniqueTypes(results: PromiseSettledResult<Array<{ value: string; label: string }>>[]) {
  const seen = new Set<string>();
  const types: MaterialTypeOption[] = [];
  for (const result of results) {
    if (result.status === "rejected") continue;
    for (const item of result.value) {
      if (!item.value || seen.has(item.value)) continue;
      seen.add(item.value);
      types.push({ value: item.value, label: item.label || item.value });
    }
  }
  return types;
}

export function useHomeFeed(): HomeFeed {
  const [featured, setFeatured] = useState<CommunityNote[]>([]);
  const [latest, setLatest] = useState<CommunityNote[]>([]);
  const [notePool, setNotePool] = useState<CommunityNote[]>([]);
  const [materials, setMaterials] = useState<MaterialItem[]>([]);
  const [materialTypes, setMaterialTypes] = useState<MaterialTypeOption[]>([]);
  const [topics, setTopics] = useState<CommunityTopic[]>([]);
  const [loading, setLoading] = useState(true);
  const [materialsReady, setMaterialsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const materialsRequest = libraryApi.getMaterials({ page: 1, pageSize: 24, enabled: true }).then((result) => {
      setMaterials(result.items);
      return result;
    }).finally(() => setMaterialsReady(true));
    const [recommendedRes, recentRes, materialsRes, effectTypes, otherTypes, topicsRes] = await Promise.allSettled([
      communityApi.notes({ page: 1, pageSize: 12, sort: "recommend" }),
      communityApi.notes({ page: 1, pageSize: 12, sort: "latest" }),
      materialsRequest,
      libraryApi.getMaterialTypes(),
      libraryApi.getMaterialTypes("other"),
      communityApi.topics({ page: 1, pageSize: 12, popular: true }),
    ]);
    const recommended = itemsOf(recommendedRes);
    const recent = itemsOf(recentRes);
    const featuredNotes = pickCoveredNotes(recommended, 4, recent);
    const featuredIds = new Set(featuredNotes.map((item) => item.id));
    const latestNotes = pickCoveredNotes(
      recent.filter((item) => !featuredIds.has(item.id)),
      4,
      recommended.filter((item) => !featuredIds.has(item.id)),
    );
    setFeatured(featuredNotes);
    setLatest(latestNotes);
    setNotePool(pickCoveredNotes(recommended, 24, recent));
    setMaterials(itemsOf(materialsRes));
    setMaterialTypes(uniqueTypes([effectTypes, otherTypes]));
    setTopics(itemsOf(topicsRes).filter((item) => item.name && item.name !== "全部"));
    const failed = [recommendedRes, recentRes, materialsRes, effectTypes, otherTypes, topicsRes]
      .filter((result) => result.status === "rejected").length;
    setError(failed === 6 ? "首页内容暂时无法加载" : failed ? "部分内容暂时无法加载" : null);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { featured, latest, notePool, materials, materialTypes, topics, materialsReady, loading, error, reload: load };
}
